/* eslint-disable react/no-unescaped-entities */
import { Box, Typography } from "@mui/material";
import {
  ACCENT,
  ACCENT_DARK,
  ACCENT_TINT,
  INK,
  INK_SECONDARY,
  INK_MUTED,
  HAIRLINE,
  SURFACE,
  FONT,
  WEIGHT,
  withAlpha,
} from "../theme/tokens";

// Distinct, soft-tinted hue per change category.
const CATEGORY_COLORS = {
  Administration: "#7C6AED", // violet
  Gateway: "#3B82F6", // blue
  "Frontend/UI": ACCENT, // teal
  Experiments: "#E0922F", // amber
  Models: "#6366F1", // indigo
};
const DEFAULT_CATEGORY_COLOR = "#64748B"; // slate

const changelogData = [
  {
    version: "1.3.0",
    date: "August 29, 2026",
    sections: [
      {
        title: "Frontend/UI",
        items: [
          "Harden admin-only monitoring/email UI access by adding protected proxy routes with identity check to the BFF frontend server",
          "Removed stale agent interaction interfaces; Added components to expose MCP services: BFF MCP routes and OAuth consent page for authentication",
          "Aligned frontend read utility call sites with the new backend endpoint implementation",
          "Aligned experiment creation UI to work with the new receipt-backed YAML submissions; Removed separate frontend config translation",
        ],
      },
      {
        title: "Experiments",
        items: [
          "Made experiment config YAML validated by the authoritative EFabric workspace backend; Experiment creation now requires explicit validation receipt signed by backend",
          "Rewrote backend utility endpoints to serve frontend services and the MCP server via unified internal tool entrance",
          "Rewrote fs_tools to sequentially open files following file descriptors to prevent pathname drift issue/attack",
          "Implemented an MCP server to expose common EFabric cluster operations as MCP tools, enabling agents to operate experiments on behalf of users",
        ],
      },
      {
        title: "Administration",
        items: [
          "Update new node labeling to prevent NVIDIA GPU operator from scheduling components to CPU workers",
          "Move separated Supabase service credentials to centralized Kubernetes configMap/Secret, supporting Supabase project branching for development",
        ],
      },
    ],
  },
  {
    version: "1.2.0",
    date: "July 31, 2026",
    sections: [
      {
        title: "Frontend/UI",
        items: [
          "Implemented explicit validation of the experiment config before actual submission; Added a validation message box that provides feedback and errors",
          "Removed tab gating that blocks users from freely browsing the sections of the experiment creation forms; Replaced with stepper indicators warning missing/invalid inputs",
          "Added config import support to Workspace creation; Both Workspace/Job config import triggers backend validation",
        ],
      },
      {
        title: "Experiments",
        items: [
          "Cleaned up unused backend endpoints; Removed unused cluster resource status collector",
          "Backend implemented experiment config validation that guards experiment creation",
          "Updated bootstrap helper expectations to align with the new host aliases",
        ],
      },
      {
        title: "Administration",
        items: [
          "Adjusted PromQL queries of experiment summary dashboard & per-pod dashboard so they properly display job container stats",
          "Added unit test CI actions to the EFabric experiment backend, covering bootstrap scripts, constants, and config validation",
        ],
      },
    ],
  },
  {
    version: "1.1.0",
    date: "July 25, 2026",
    sections: [
      {
        title: "Frontend/UI",
        items: [
          "Added EFabric cluster storage monitoring dashboard to display JuiceFS/SeaweedFS storage status",
          "Adjusted Side menu icons to avoid confusion between components",
        ],
      },
      {
        title: "Experiments",
        items: [
          "Added user directory permission repair script to protect user-owned paths from being broken by EFabric admin operations",
        ],
      },
      {
        title: "Administration",
        items: [
          "Enabled GitLab Runner cache; Extended GitLab runner resources to support CI jobs",
          "Configured Prometheus to scrape metrics from JuiceFS CSI mount pods",
        ],
      },
    ],
  },
  {
    version: "1.0.0",
    date: "July 22, 2026",
    sections: [
      {
        title: "Frontend/UI",
        items: [
          "Bump version to v1.0.0",
          "Fixed CPU core limit derivation of GPU Experiments to use static partition resource specification instead of runtime status",
          "Added documentation pages for EFabric models and distributed experiments",
        ],
      },
      {
        title: "Experiments",
        items: [
          "Added ownership patch to mamba initialization to prevent user from lacking write permission",
          "Refactored Workspace/Job entrypoint steps to common bootstrap scripts, remove separate job configMap template",
          "Backend switched to immutable per-Experiment script configMap to ensure tenant isolation and backward compatibility",
          "Implemented model cache projection & eviction to Experiment entrypoint scripts",
          "Added host alias setup to Experiment bootstrap process, providing simple worker hostnames",
        ],
      },
      {
        title: "Administration",
        items: [
          "Enabled HTTPS for website",
          "Adjusted JuiceFS CSI config to make node local caching less aggressive",
          "Added scheduled filer metadata backups",
          "Defined and applied global cluster firewall strategy to prevent external network attacks",
        ],
      },
    ],
  },
  {
    version: "0.6.0",
    date: "July 10, 2026",
    sections: [
      {
        title: "Frontend/UI",
        items: [
          "Refreshed the signed-in experience with shared design tokens, a polished TopBar/sidebar, sticky page headers, and more consistent page spacing across the workspace.",
          "Added and refined EFabric catalog pages for compute resources, workflow templates, datasets, public models, and the Home Viewer storage browser.",
          "Introduced the Compute Resources page with resource groups, live partition utilization, assigned-group expansion, and clearer runtime limit display.",
          "Improved workspace and job creation with dynamic resource limits, fixed walltime messaging, registry-backed image validation, validation for names and resource capacity, EFabric config download/upload, and clearer resource sliders.",
          "Expanded workflow image and file browsing with workflow inventories, image preview support in Markdown, GitLab-backed pagination for large catalogs, full-space Monaco file viewing, and richer file previews.",
          "Improved Markdown rendering with GitHub-style callouts, shared prose styling, safer relative asset handling, and better model-card and file previews.",
          "Modernized My Experiments tables with wider action columns, aligned job/workspace tables, resource chips, lighter command cells, and paging, sorting, and filtering improvements.",
          "Improved admin UX with sortable/searchable user tables, reused email/name display helpers, Monaco-backed message editing, and a cleaner Cluster Topology map with zoomable minimap and better recentering.",
          "Refactored authentication screens around shared brand/form content and reusable validation helpers.",
          "Removed legacy workflow catalog components and unused UI files while aligning catalog card grids, page layouts, colors, icons, and helper text across the app.",
        ],
      },
      {
        title: "Models",
        items: [
          "Added the public Models page for browsing cached Hugging Face models with search, sorting, pagination, pipeline and organization filters, parameter-size filtering, and direct Hugging Face links.",
          "Added model cards and detail drawers with cache metadata, revisions, tags, downloads, likes, size, parameter counts, and rendered model-card Markdown previews.",
          "Added an admin Models panel for cache status, scans, imports, dry-run eviction, deletion, refreshes, and real-time import progress polling.",
          "Added shared model-cache API hooks for listing cached models, loading per-model details, importing revisions, checking import status, deleting caches, scanning caches, and evicting old entries.",
        ],
      },
      {
        title: "Administration",
        items: [
          "Guarded admin message sending with authenticated API calls and an explicit admin-role check.",
          "Refreshed user and group membership data after resource group grants or revocations so admin user tables stay current.",
          "Implemented walltime-limit experiment eviction.",
          "Lowered the default user home capacity.",
          "Changed aggregated resource status to derive from Kueue bookkeeping.",
          "Added partition-specific queue aggregation and CPU/RAM usage reporting.",
          "Added group-specific partition slices for resource status aggregation.",
          "Removed legacy ssh-dss keys from the SSH key allowlist.",
        ],
      },
      {
        title: "Experiments",
        items: [
          "Added the EFabric job backend with persistent job records.",
          "Added job log handling, timeout status, unified deletion, and pending-time estimates.",
          "Added support for multi-worker statefulset-based jobs.",
          "Moved interactive workspace scheduling onto Kueue LocalQueues.",
          "Updated workspace and job manifests to share Kueue-aware resource handling.",
          "Unified CPU and GPU workspace manifests.",
          "Allowed GPU workspaces to request CPU cores.",
          "Reported per-experiment resource configuration back to the UI.",
          "Factored out image manifests from experiment resource generation.",
          "Moved experiment setup into backend-injected entrypoint scripts.",
          "Adjusted downloadable experiment configuration fields for resource group names and experiment type.",
          "Renamed job configuration fields for datasets and node counts.",
          "Blocked workspace and job creation from advancing when selected container images are not recognized in the EFabric registry catalog.",
          "Added a separate Kubernetes API client for pod exec.",
          "Fixed the home initialization job template.",
          "Removed user IDs from service and network-policy names to provide stable DNS names.",
          "Added safer label selectors and hostname fallback behavior.",
        ],
      },
    ],
  },
  {
    version: "0.5",
    date: "March 18, 2026",
    sections: [
      {
        title: "Administration",
        items: [
          "EFabric migrated to the Research Backbone Network.",
          "Introduced experiment expiration mechanism to warn then clean up unused experiments.",
          "Updated User management to display assigned partitions and added partition name as a search key.",
          "Improved the layout of cluster dashboards.",
        ],
      },
      {
        title: "Gateway",
        items: [
          "Fixed multiple resource leak bugs.",
          "Switch to using epoll() for better handling forwarding channels.",
          "Backend supporting multiple gateways and runtime gateway discovery.",
        ],
      },
      {
        title: "Frontend/UI",
        items: [
          "Fix experiment status not being updated when experiments are terminated.",
          "Improve the experiment creation form for easier configuration.",
          "Update Documentation to introduce minimal MPI setup.",
          "Improve Docs writing.",
        ],
      },
      {
        title: "Experiments",
        items: [
          "Moved workspace SSHD setup to before Python env to avoid liveness probe failure.",
          "Fixed workspace resource template for guaranteed QoS class.",
          "Added basic network tools to the workspace image.",
          "Increased /dev/shm size to half of the requested workspace memory.",
          "Enabled parallel workspace pod creation to avoid timeout when creating multi-worker experiments.",
          "Cluster status collector ignoring non-EFabric pods and non-schedulable nodes.",
          "Fixed dataset PVC reference bug that caused pre-mature deletion of PVCs.",
        ],
      },
    ],
  },
  {
    version: "0.4",
    date: "December 5, 2025",
    sections: [
      {
        title: "Administration",
        items: [
          "Globally unique EFabric user names/ids for resource management & isolation.",
          "Cluster & per-node monitoring dashboards implemented with Prometheus & Grafana.",
          "Overall & per-experiment monitoring dashboards.",
          "Basic alerting system using Grafana Alertmanager for checking health & memory pressure.",
          "Tracking cluster-wide resource usage from Prometheus metrics.",
          "Per-user resource partition management: grant/revoke GPU products.",
          "Cluster topology visualization using React Flow.",
          "Send messages to selected EFabric users via admin dashboard.",
        ],
      },
      {
        title: "Frontend/UI",
        items: [
          "Completely redesigned UI for better user experience and accessibility.",
          "Improve User Profile page to display EFabric identities and assigned resources.",
          "Switch service architecture to using BFF (Backend For Frontend) pattern for better maintainability.",
        ],
      },
      {
        title: "Experiments",
        items: [
          "Support experiment-local burst storage space.",
          "Allow users to specify custom memory requirements for experiments.",
        ],
      },
    ],
  },
  {
    version: "0.3",
    date: "September 12, 2025",
    sections: [
      {
        title: "Frontend/UI",
        items: ["Improve active experiment views and status display."],
      },
      {
        title: "Experiments",
        items: [
          "Refactor experiment resources to statefulsets, allowing pinning to burst storage.",
          "Added support to local burst storage for experiments.",
        ],
      },
    ],
  },
  {
    version: "0.2.1",
    date: "September 11, 2025",
    sections: [
      {
        items: ["Introduced EFabric release notes."],
      },
      {
        title: "Frontend/UI",
        items: [
          "Added a username that can be specified by users.",
          "Topology view adjustment to EFabric admin dashboard.",
          "Display decimals with one digit on homepage and admin monitor.",
          "Solved the issue of slow topology loading.",
        ],
      },
    ],
  },
  {
    version: "0.2",
    date: "September 7, 2025",
    sections: [
      {
        title: "Frontend/UI",
        items: ["Added separate menus for CPU-only & GPU configurations."],
      },
      {
        title: "Experiments",
        items: ["Added support to CPU-only workspaces."],
      },
    ],
  },
  {
    version: "0.1",
    date: "September 4, 2025",
    sections: [
      {
        title: "Frontend/UI",
        items: [
          "User registration.",
          "Minimal user profile visualization.",
          "Controller UI for creating/checking/removing experiment workspaces.",
          "Cluster topology dashboard.",
          "Cluster resource utilization dashboard.",
          "Cluster storage status dashboard supported by Ceph.",
          "Cluster experiment runtime status dashboard.",
        ],
      },
      {
        title: "Experiments",
        items: [
          "Aggregates and exposes available GPU products/availability.",
          "Added basic workspace creation & termination.",
          "GPU-centric resource configuration.",
          "Added support to dataset mount during creation.",
          "Periodic workspace status collection.",
        ],
      },
    ],
  },
];

const CategoryTag = ({ title }) => {
  const color = CATEGORY_COLORS[title] || DEFAULT_CATEGORY_COLOR;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        mb: 1,
        borderRadius: "999px",
        bgcolor: withAlpha(color, 0.12),
        color,
        fontSize: FONT.xs,
        fontWeight: WEIGHT.bold,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      {title}
    </Box>
  );
};

const ChangeItems = ({ items }) => (
  <Box
    component="ul"
    sx={{
      listStyle: "none",
      m: 0,
      p: 0,
      display: "flex",
      flexDirection: "column",
      gap: 0.75,
    }}
  >
    {items.map((item, i) => (
      <Box
        component="li"
        key={i}
        sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}
      >
        <Box
          sx={{
            mt: "9px",
            width: 5,
            height: 5,
            borderRadius: "50%",
            bgcolor: INK_MUTED,
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{ fontSize: FONT.md, color: INK_SECONDARY, lineHeight: 1.6 }}
        >
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

const Changelog = () => {
  return (
    <Box
      sx={{ flexGrow: 1, bgcolor: "background.default", py: { xs: 4, md: 6 } }}
    >
      <Box sx={{ maxWidth: 860, mx: "auto", px: { xs: 3, md: 4 } }}>
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <Typography
            sx={{
              fontSize: FONT.xs,
              fontWeight: WEIGHT.bold,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: ACCENT_DARK,
            }}
          >
            Release notes
          </Typography>
          <Typography
            component="h1"
            sx={{
              mt: 1,
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: WEIGHT.bold,
              letterSpacing: "-0.02em",
              color: INK,
            }}
          >
            Changelog
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontSize: FONT.lg,
              color: INK_SECONDARY,
              maxWidth: 620,
            }}
          >
            Everything new on EFabric — newest releases first.
          </Typography>
        </Box>

        {/* Timeline */}
        <Box>
          {changelogData.map((release, index) => {
            const isLast = index === changelogData.length - 1;
            const isLatest = index === 0;
            return (
              <Box
                key={release.version}
                id={`v${release.version}`}
                sx={{
                  position: "relative",
                  pl: { xs: 3.5, md: 4 },
                  pb: isLast ? 0 : 6,
                }}
              >
                {/* Timeline spine */}
                {!isLast && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 7,
                      top: 20,
                      bottom: 0,
                      width: "2px",
                      bgcolor: HAIRLINE,
                    }}
                  />
                )}
                {/* Node */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 4,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: SURFACE,
                    border: `2px solid ${isLatest ? ACCENT : HAIRLINE}`,
                    boxShadow: isLatest
                      ? `0 0 0 4px ${withAlpha(ACCENT, 0.12)}`
                      : "none",
                  }}
                />

                {/* Release header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 2.5,
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                  >
                    <Typography
                      component="h2"
                      sx={{
                        fontSize: FONT.xl,
                        fontWeight: WEIGHT.bold,
                        color: INK,
                      }}
                    >
                      Version {release.version}
                    </Typography>
                    {isLatest && (
                      <Box
                        component="span"
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: "999px",
                          bgcolor: ACCENT_TINT,
                          color: ACCENT_DARK,
                          fontSize: FONT.xs,
                          fontWeight: WEIGHT.bold,
                          letterSpacing: "0.02em",
                        }}
                      >
                        Latest
                      </Box>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: FONT.sm, color: INK_MUTED }}>
                    {release.date}
                  </Typography>
                </Box>

                {/* Sections */}
                {release.sections.map((section, sectionIndex) => (
                  <Box
                    key={sectionIndex}
                    sx={{ mt: sectionIndex === 0 ? 0 : 3 }}
                  >
                    {section.title && <CategoryTag title={section.title} />}
                    <ChangeItems items={section.items} />
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default Changelog;
